import { useEffect, useState } from "react";
import { Route, Switch } from "wouter";
import { Dashboard } from "./pages/Dashboard";
import { SignIn } from "./pages/SignIn";
import { Editor } from "./pages/Editor";
import { AcceptInvite } from "./pages/AcceptInvite";
import { Settings } from "./pages/Settings";
import { Projects } from "./pages/Projects";
import { Published } from "./pages/Published";
import { Templates } from "./pages/Templates";
import { Import } from "./pages/Import";
import { RequireAuth } from "./components/auth/RequireAuth";
import { SettingsModal, type SettingsSection } from "./components/settings/SettingsModal";
import { CommandPalette } from "./components/clui/CommandPalette";
import { NotificationsPanel } from "./components/notifications/NotificationsPanel";

export function App() {
  return (
    <>
      <Switch>
        <Route path="/">
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        </Route>
        <Route path="/signin" component={SignIn} />
        <Route path="/accept/:token" component={AcceptInvite} />
        <Route path="/settings">
          <RequireAuth>
            <Settings />
          </RequireAuth>
        </Route>
        <Route path="/projects">
          <RequireAuth>
            <Projects />
          </RequireAuth>
        </Route>
        <Route path="/published">
          <RequireAuth>
            <Published />
          </RequireAuth>
        </Route>
        <Route path="/templates">
          <RequireAuth>
            <Templates />
          </RequireAuth>
        </Route>
        <Route path="/import">
          <RequireAuth>
            <Import />
          </RequireAuth>
        </Route>
        <Route path="/editor/:projectId">
          {(params) => (
            <RequireAuth>
              <Editor key={params.projectId} />
            </RequireAuth>
          )}
        </Route>
        <Route>
          <NotFound />
        </Route>
      </Switch>
      <SettingsModalHost />
      <CommandPaletteHost />
      <NotificationsHost />
    </>
  );
}

/**
 * Mounts the Notifications popover at the root. Listens for the
 * `vp:notifications:open` event from the NavRail / command palette.
 */
function NotificationsHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onOpen() {
      setOpen((v) => !v);
    }
    window.addEventListener("vp:notifications:open", onOpen);
    return () => window.removeEventListener("vp:notifications:open", onOpen);
  }, []);
  return <NotificationsPanel open={open} onClose={() => setOpen(false)} />;
}

/**
 * Mounts the CLUI command palette at the root. Cmd-K / Ctrl-K toggles
 * open; `vp:clui:open` event also opens it.
 */
function CommandPaletteHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    function onOpenEvent() {
      setOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("vp:clui:open", onOpenEvent);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("vp:clui:open", onOpenEvent);
    };
  }, []);
  return <CommandPalette open={open} onClose={() => setOpen(false)} />;
}

/**
 * Mounts the Settings modal at the root and listens for the global
 * `vp:settings:open` event so any component can open it without a
 * Context dance. Event detail.section optionally focuses a specific
 * panel (e.g. {@link SettingsSection} "promotions").
 */
function SettingsModalHost() {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<SettingsSection>("workspace-overview");
  useEffect(() => {
    function onOpen(e: Event) {
      const detail = (e as CustomEvent<{ section?: SettingsSection }>).detail;
      if (detail?.section) setSection(detail.section);
      setOpen(true);
    }
    window.addEventListener("vp:settings:open", onOpen);
    return () => window.removeEventListener("vp:settings:open", onOpen);
  }, []);
  return <SettingsModal open={open} onClose={() => setOpen(false)} initialSection={section} />;
}

function NotFound() {
  return (
    <div
      style={{
        padding: 32,
        color: "var(--text-primary)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <h1>Not found</h1>
      <p>This page doesn't exist. Head <a href="/">home</a>.</p>
    </div>
  );
}
